import type { MediaObjectMeta, MediaUploadAdapter, MediaUploadResult } from "./uploadAdapter";
import {
  assertSameStorageKey,
  buildStorageKey,
  isHttpsUrl,
  joinPublicUrl,
} from "./storageKey";
import type { MediaSignRequest, MediaSignResponse } from "./signTypes";
import { MediaUploadError } from "./signTypes";

export interface CloudUploadConfig {
  provider: "s3" | "r2" | "supabase";
  /**
   * Real sign endpoint, e.g.
   * `https://un8-media-sign.<subdomain>.workers.dev/media/sign`
   * or local `http://127.0.0.1:8787/media/sign`
   */
  signUrl: string;
  /** CDN / public R2 base, e.g. https://media.un8.app */
  publicBaseUrl?: string;
  /** Shared SIGN_SECRET or JWT — sent as Bearer on sign request */
  authToken?: string | (() => string | null | Promise<string | null>);
  /** Extra headers for sign request */
  headers?: Record<string, string>;
  /** Defaults to PUT (R2/S3 presign) */
  uploadMethod?: "PUT" | "POST";
  fetchImpl?: typeof fetch;
}

async function resolveAuthToken(
  token: CloudUploadConfig["authToken"]
): Promise<string | null> {
  if (!token) return null;
  if (typeof token === "function") return token();
  return token;
}

function allowDevHttp(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.protocol === "http:" &&
      (u.hostname === "localhost" ||
        u.hostname === "127.0.0.1" ||
        u.hostname === "[::1]")
    );
  } catch {
    return false;
  }
}

function sanitizePublicUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (isHttpsUrl(trimmed) || allowDevHttp(trimmed)) return trimmed;
  return "";
}

/**
 * Cloud adapter wired to a real `POST {signUrl}` endpoint
 * (see `workers/media-sign`).
 *
 * Flow: buildStorageKey → sign → PUT bytes → { storageKey, publicUrl }
 */
export function createCloudUploadAdapter(config: CloudUploadConfig): MediaUploadAdapter {
  if (!config.signUrl?.trim()) {
    throw new MediaUploadError(
      "createCloudUploadAdapter: signUrl is required (Worker /media/sign)",
      "config"
    );
  }

  const fetchFn = config.fetchImpl ?? fetch.bind(globalThis);
  const publicByKey = new Map<string, string>();

  return {
    id: config.provider,

    async upload({ userId, asset, file }): Promise<MediaUploadResult> {
      const expectedKey = buildStorageKey({
        userId,
        kind: asset.kind,
        assetId: asset.id,
        fileName: asset.fileName,
        mimeType: asset.mimeType || file.type,
      });
      const contentType = asset.mimeType || file.type || "application/octet-stream";

      const auth = await resolveAuthToken(config.authToken);
      const signHeaders: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(config.headers || {}),
      };
      if (auth) {
        signHeaders.Authorization = auth.startsWith("Bearer ")
          ? auth
          : `Bearer ${auth}`;
      }

      const signBody: MediaSignRequest = {
        storageKey: expectedKey,
        contentType,
        sizeBytes: file.size,
        userId,
        assetId: asset.id,
        kind: asset.kind,
        fileName: asset.fileName,
        provider: config.provider,
      };

      let signRes: Response;
      try {
        signRes = await fetchFn(config.signUrl, {
          method: "POST",
          headers: signHeaders,
          body: JSON.stringify(signBody),
        });
      } catch (e) {
        throw new MediaUploadError(
          e instanceof Error
            ? `Sign endpoint unreachable: ${e.message}`
            : "Sign endpoint unreachable",
          "sign"
        );
      }

      if (!signRes.ok) {
        const body = await signRes.text().catch(() => "");
        let detail = body.slice(0, 240);
        try {
          const j = JSON.parse(body) as { error?: string };
          if (j.error) detail = j.error;
        } catch {
          /* keep raw */
        }
        throw new MediaUploadError(
          `Sign failed (${signRes.status})${detail ? `: ${detail}` : ""}`,
          "sign",
          signRes.status
        );
      }

      let signed: MediaSignResponse & { storageKey?: string; headers?: Record<string, string> };
      try {
        signed = (await signRes.json()) as typeof signed;
      } catch {
        throw new MediaUploadError("Sign response was not JSON", "sign", signRes.status);
      }

      if (!signed.uploadUrl || typeof signed.uploadUrl !== "string") {
        throw new MediaUploadError("Sign response missing uploadUrl", "sign", signRes.status);
      }

      if (signed.storageKey) {
        assertSameStorageKey(expectedKey, signed.storageKey);
      }

      const method = signed.uploadMethod ?? config.uploadMethod ?? "PUT";
      const putHeaders: Record<string, string> = {
        "Content-Type": contentType,
        ...(signed.uploadHeaders || signed.headers || {}),
      };

      let put: Response;
      try {
        put = await fetchFn(signed.uploadUrl, {
          method,
          headers: putHeaders,
          body: file,
        });
      } catch (e) {
        throw new MediaUploadError(
          e instanceof Error
            ? `Storage PUT failed: ${e.message}`
            : "Storage PUT failed",
          "put"
        );
      }

      if (!put.ok) {
        throw new MediaUploadError(
          `Storage upload failed (${put.status})`,
          "put",
          put.status
        );
      }

      const composed = config.publicBaseUrl
        ? joinPublicUrl(config.publicBaseUrl, expectedKey)
        : "";
      const publicUrl = sanitizePublicUrl(signed.publicUrl || composed);

      if (publicUrl) publicByKey.set(expectedKey, publicUrl);

      const meta: MediaObjectMeta = {
        storageKey: expectedKey,
        userId,
        assetId: asset.id,
        kind: asset.kind,
        fileName: asset.fileName,
        mimeType: contentType,
        sizeBytes: file.size,
        createdAt: new Date().toISOString(),
      };

      return {
        storageKey: expectedKey,
        publicUrl,
        meta,
        via: "cloud",
      };
    },

    async getPreviewUrl(storageKey) {
      const remembered = publicByKey.get(storageKey);
      if (remembered) return remembered;
      if (config.publicBaseUrl) {
        return joinPublicUrl(config.publicBaseUrl, storageKey);
      }
      return null;
    },
  };
}

/** @deprecated alias */
export const createCloudUploadAdapterStub = createCloudUploadAdapter;
